import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import AdminNav from '../../components/admin/AdminNav';
import { useAuth } from '../../context/AuthContext';
import {
  getEngagementStats,
  getSlipFlags,
  getRecentActivity,
  getHubEventsCache,
  runHubSync,
  runDailyContentDispatchNow,
  finishUnusualActivity,
  referUnusualActivity,
  explainUnusualActivity,
  getStripeHealth,
} from '../../services/admin.service';

/**
 * Team365 Overview — the real business-vitals landing page.
 *
 * Every number here comes from a real query against real data — nothing is
 * mocked or estimated. Tools this app doesn't actually have an integration
 * for yet (Funnels, Canva, Social Scheduler, etc.) are shown honestly as
 * "Coming soon", same pattern as My Devices on the member side, rather than
 * as dead buttons that look live.
 */
export default function AdminOverview() {
  const { user } = useAuth();
  const [stats,    setStats]    = useState(null);
  const [flags,    setFlags]    = useState([]);
  const [activity, setActivity] = useState([]);
  const [events,   setEvents]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [syncing,  setSyncing]  = useState(false);
  const [syncMsg,  setSyncMsg]  = useState(null);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchMsg, setDispatchMsg] = useState(null);
  const [showFlags, setShowFlags] = useState(false);
  const [stripeHealth, setStripeHealth] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, flagsData, activityData, eventsData] = await Promise.all([
        getEngagementStats(),
        getSlipFlags(),
        getRecentActivity(),
        getHubEventsCache(),
      ]);
      setStats(statsData);
      setFlags(flagsData.flags || []);
      setActivity(activityData.activity || []);
      setEvents(eventsData || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Separate from load() so a Stripe hiccup never blanks the rest of the page.
  useEffect(() => { getStripeHealth().then(setStripeHealth).catch(() => setStripeHealth(null)); }, []);

  const handleHubSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const { summary } = await runHubSync();
      setSyncMsg(
        summary?.skipped
          ? `Skipped — ${summary.reason}`
          : `Done — ${summary.hubEvents} Hub event(s) synced.`
      );
      load();
    } catch (err) {
      setSyncMsg(err.response?.data?.error || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const handleRunDispatch = async () => {
    setDispatching(true);
    setDispatchMsg(null);
    try {
      const result = await runDailyContentDispatchNow();
      setDispatchMsg(
        `${result.sent} sent, ${result.skippedAlreadySentToday} already sent today, ${result.skippedNoContent} no content for their day, ${result.failed} failed (of ${result.total} enrolled).`
      );
    } catch (err) {
      setDispatchMsg(err.response?.data?.error || 'Dispatch run failed.');
    } finally {
      setDispatching(false);
    }
  };

  const upcoming = events
    .filter(e => new Date(e.event_datetime || e.scheduled_date) >= new Date())
    .slice(0, 5);

  const realTools = [
    { label: 'Member / Prospect Files', to: '/admin/members' },
    { label: 'Courses',                 to: '/admin/courses' },
    { label: 'My Hive (Community)',     to: '/admin/community' },
    { label: 'HMMMM? / Announcements',  to: '/admin/announcements' },
    { label: '🎥 Events & Zoom Control Centre', to: '/admin/hub-events' },
    { label: '✉️ Groups & Bulk Email',          to: '/admin/bulk-email' },
    { label: '🎨 Canva design requests', to: '/admin/requests?category=design_request' },
    ...(user?.can_manage_affiliates ? [{ label: 'Affiliates', to: '/admin/affiliates' }] : []),
  ];

  const comingSoonTools = [
    'Funnels', 'Website Pages', 'Social Scheduler',
    'Substack & Pinterest', 'Automations',
  ];

  return (
    <DashboardLayout>
      <PageHeading title="Team365" subtitle="Business, at a glance." className="mb-5" />

      <AdminNav />

      {/* Jenn, 2026-10-12: "We can't sell if we don't have that working." Bold on purpose. */}
      {stripeHealth && stripeHealth.ok === false && (
        <div role="alert" className="mb-6 rounded-2xl border-4 border-reset-terracotta bg-white p-5">
          <p className="text-xl font-extrabold text-reset-brown">Stripe is not connected.</p>
          <p className="mt-1 font-bold text-reset-brown">
            New members can pay, but they won't get a welcome or an account, and payments aren't recorded, until this is fixed.
          </p>
          <p className="mt-1 text-sm text-reset-grey">
            Since {new Date(stripeHealth.since).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}.
            Fix: Backend → Server Credentials → "Stripe updates" → Reconnect. This goes away by itself once Stripe reaches us again.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <Link to="/admin/backend" className="px-5 py-2.5 bg-reset-terracotta text-white text-sm font-bold rounded-full">Open Backend</Link>
            <Link to="/admin/todos" className="px-5 py-2.5 border-2 border-reset-terracotta text-reset-brown text-sm font-bold rounded-full">See the To-Do</Link>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-200 rounded-xl" />)}
        </div>
      ) : (
        <>
          {/* ── Vitals ─────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <StatCard label="Total members"   value={stats?.total_members} to="/admin/members" />
            <StatCard label="Active"          value={stats?.active_members} to="/admin/members?filter=active" />
            <StatCard label="Paying members"  value={stats?.paying_members} to="/admin/members?filter=paying" />
            <StatCard label="At founder price $49.95" value={stats?.founder_price_members} to="/admin/members?filter=founder_price" />
            <StatCard label="At monthly $20.58" value={stats?.regular_price_members} to="/admin/members?filter=regular_price" />
            <StatCard label="Checked in (7d)" value={stats?.checked_in_7d} />
            <StatCard label="Nudges (7d)"     value={stats?.nudges_sent_7d} />
            <StatCard
              label="Needs attention"
              value={flags.length}
              highlight={flags.length > 0}
              onClick={flags.length > 0 ? () => setShowFlags(v => !v) : undefined}
              hint={flags.length > 0 ? 'Click to see who, and why' : 'No one is flagged right now'}
            />
            <StatCard
              label="Avg score today"
              value={stats?.avg_score_today != null ? stats.avg_score_today.toFixed(1) : '—'}
              unit="/10"
              hint="Every member's daily wellness score, averaged — computed overnight from their sector check-in data"
            />
          </div>

          {showFlags && flags.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
              <p className="font-semibold text-sm text-reset-brown mb-3">
                Who needs attention, and why
              </p>
              <div className="space-y-2">
                {flags.map(f => (
                  <Link
                    key={f.memberId}
                    to={`/admin/members/${f.memberId}`}
                    className="flex items-center justify-between text-sm bg-reset-cream rounded-lg px-3 py-2 hover:bg-reset-creamDark transition-colors"
                  >
                    <span className="font-medium text-reset-brown">{f.name || f.email}</span>
                    <span className="text-reset-grey text-xs text-right ml-3">{f.reason}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* ── Unusual activity ──────────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
            <p className="font-semibold text-sm text-reset-brown mb-3">
              Unusual activity (last 14 days)
            </p>
            {activity.length === 0 ? (
              <p className="text-sm text-gray-400">Nothing to report — no cancellations, reactivations, failed sends, or missing content.</p>
            ) : (
              <div className="space-y-1">
                {activity.map(a => <UnusualActivityRow key={a.id} activity={a} onActed={load} />)}
              </div>
            )}
          </div>

          {/* ── Daily Content Dispatch (real send engine) ────────
              Runs automatically at 8am ET; this button re-runs it on demand
              for verifying the engine during the beta/deglitching week
              without waiting for the cron. */}
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between mb-1">
              <p className="font-semibold text-sm text-reset-brown">Daily Content Dispatch</p>
              <button
                onClick={handleRunDispatch}
                disabled={dispatching}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta transition-colors disabled:opacity-50"
              >
                {dispatching ? 'Running…' : '▶ Run Now'}
              </button>
            </div>
            <p className="text-xs text-gray-400">Runs automatically every day at 8am ET. Only touches members moved into the new system (Real Daily Send turned on, member profile).</p>
            {dispatchMsg && <p className="text-xs text-reset-sage mt-2">{dispatchMsg}</p>}
          </div>

          {/* ── Upcoming Hub Events ───────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm text-reset-brown">Upcoming Hub Events</p>
              <div className="flex items-center gap-3">
                {syncMsg && <span className="text-xs text-gray-500">{syncMsg}</span>}
                <button
                  onClick={handleHubSync}
                  disabled={syncing}
                  className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta transition-colors disabled:opacity-50"
                >
                  {syncing ? 'Syncing…' : '↻ Sync Now'}
                </button>
              </div>
            </div>
            {upcoming.length === 0 ? (
              <p className="text-sm text-gray-400">Nothing scheduled right now.</p>
            ) : (
              <div className="space-y-2">
                {upcoming.map(e => (
                  <div key={e.id} className="flex items-center justify-between text-sm py-1">
                    <span className="text-reset-brown">{e.title}</span>
                    <span className="text-xs text-gray-400">
                      {e.event_datetime ? new Date(e.event_datetime).toLocaleString(undefined, { hour12: true }) : e.scheduled_date}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Tools grid ─────────────────────────────────────── */}
          <p className="font-semibold text-sm text-reset-brown mb-3">Your tools</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {realTools.map(t => (
              <Link
                key={t.label}
                to={t.to}
                className="bg-white rounded-xl shadow-sm p-4 text-center hover:-translate-y-0.5 transition-transform"
              >
                <p className="text-sm font-medium text-reset-brown">{t.label}</p>
              </Link>
            ))}
            {comingSoonTools.map(label => (
              <div
                key={label}
                title="Not built yet — needs its own integration"
                className="bg-gray-50 rounded-xl p-4 text-center opacity-60 cursor-not-allowed"
              >
                <p className="text-sm font-medium text-gray-400">{label}</p>
                <p className="text-[10px] text-gray-400 mt-1">Coming soon</p>
              </div>
            ))}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

const ACTIVITY_LABEL = {
  member_deactivated: { text: 'cancelled/lapsed', color: 'text-reset-terracotta' },
  member_reactivated: { text: 'reactivated', color: 'text-green-600' },
  DAILY_CONTENT_MISSING: { text: 'content missing — nothing sent', color: 'text-reset-terracotta' },
  HUB_ANNOUNCEMENT_MISSING: { text: 'Thought for the Day missing — showing an older day instead', color: 'text-reset-terracotta' },
  ACTIVITY_SEND_FAILED: { text: 'activity send failed', color: 'text-amber-600' },
  QUARTERLY_CHECKIN_SUBMITTED: { text: 'submitted a quarterly check-in — review their comparison', color: 'text-reset-sage' },
  CONTENT_INCOMPLETE: { text: 'needs finishing', color: 'text-amber-700' },
};

// Plain names for the sends that can fail (Jenn, 2026-09-29: alerts must say
// what's wrong, not show code names).
const SEND_NAMES = {
  sign_in_link_email: 'sign-in link email', password_reset_email: 'password reset email', daily_content: 'daily email',
  dashboard_reminder_email: 'dashboard reminder email', dashboard_reminder_sms: 'dashboard reminder text',
  missed_checkin_email: 'missed check-in email', missed_checkin_sms: 'missed check-in text',
  morning_checkin_email: 'morning check-in email', morning_checkin_sms: 'morning check-in text',
  replay_notify_email: 'replay-ready email', replay_notify_sms: 'replay-ready text',
  taichi_am_email: 'Tai Chi reminder email', taichi_am_sms: 'Tai Chi reminder text', taichi_daily_email: 'Tai Chi reminder email', taichi_sat_email: 'Saturday Tai Chi email',
  yoga_am_email: 'Chair Yoga reminder email', yoga_am_sms: 'Chair Yoga reminder text', yoga_pm_email: 'Chair Yoga reminder email',
  scardio_mw_email: 'Strength & Cardio reminder email',
};
const sendName = (key, channel) => SEND_NAMES[key] || `${(key || 'message').replace(/_/g, ' ')}${channel && !(key || '').includes(channel) ? ` (${channel})` : ''}`;

// The email service's own wording, translated. Anything we don't recognise
// is still shown, just after a plain lead-in.
const plainSendError = (err) => {
  const e = err || '';
  if (/domain.*not verified|not verified.*domain/i.test(e)) return "the email service refused it because the sending address wasn't verified yet";
  if (/rate limit|too many/i.test(e)) return 'too many emails were sent at once, so the service paused it';
  if (/invalid.*(email|to)|not a valid/i.test(e)) return "the member's email address doesn't look valid";
  if (/unsubscribed|suppress|bounced/i.test(e)) return "this address has bounced or unsubscribed before, so it wasn't sent";
  return e ? `the service said: ${e}` : 'no reason was given';
};

const fmtReach = (ymd) => ymd
  ? new Date(`${ymd}T12:00:00`).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })
  : null;

/**
 * One flagged item, with the three real actions Jenn asked for 2026-09-05:
 * mark finished (moves it to the Bug Log), refer it to a Team365 To-Do
 * (creates the to-do AND records the referral in the Bug Log), or record
 * an explanation (her choice — keep it here, or move it to the Bug Log
 * too). Nothing here deletes or edits the underlying audit_log row.
 */
function UnusualActivityRow({ activity: a, onActed }) {
  const [mode, setMode] = useState(null); // null | 'refer' | 'explain'
  const [text, setText] = useState('');
  const [moveToLog, setMoveToLog] = useState(false);
  const [saving, setSaving] = useState(false);

  const { text: label, color } = ACTIVITY_LABEL[a.action] || { text: a.action, color: 'text-gray-500' };
  const subject = a.name || a.email
    || (a.action === 'DAILY_CONTENT_MISSING' ? 'Daily content'
      : a.action === 'HUB_ANNOUNCEMENT_MISSING' ? 'Thought for the Day'
      : a.action === 'CONTENT_INCOMPLETE'
        ? `${a.metadata?.kind === 'announcement' ? 'Thought for the Day' : 'Daily email'} for day ${a.metadata?.day}`
        : null);

  const runAction = async (fn) => {
    setSaving(true);
    try {
      await fn();
      setMode(null);
      setText('');
      onActed();
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-b border-gray-50 last:border-0 py-1.5">
      <div className="flex items-center justify-between text-sm">
        <span>
          {a.target_id ? (
            <Link to={`/admin/members/${a.target_id}`} className="font-medium text-reset-brown hover:underline">{subject}</Link>
          ) : (
            <span className="font-medium text-reset-brown">{subject}</span>
          )}{' '}
          <span className={color}>{label}</span>
          {a.metadata?.status && <span className="text-gray-400"> — {a.metadata.status}</span>}
          {a.action === 'ACTIVITY_SEND_FAILED' && a.metadata?.toggle_key && (
            <span className="text-gray-500"> — their {sendName(a.metadata.toggle_key, a.metadata.channel)} didn't go out: {plainSendError(a.metadata.error)}.</span>
          )}
          {a.action === 'CONTENT_INCOMPLETE' && (
            <span className="text-gray-500">
              {' — '}{(a.problemsNow || []).join(', ') || 'something is missing'}.
              {a.reachDate && <> {a.metadata?.affectedMembers ? `${a.metadata.affectedMembers} member${a.metadata.affectedMembers === 1 ? '' : 's'} reach` : 'Members reach'} it {fmtReach(a.reachDate)}.</>}
              {' '}<Link to="/admin/sequence-content" className="text-reset-terracotta font-medium hover:underline">Fix it in Content Pushes</Link>
            </span>
          )}
          {a.action === 'DAILY_CONTENT_MISSING' && (
            <span className="text-gray-400"> — day {a.metadata?.day}, {a.metadata?.affectedMembers} member(s) not sent</span>
          )}
          {a.action === 'HUB_ANNOUNCEMENT_MISSING' && (
            <span className="text-gray-400"> — day {a.metadata?.day}, {a.metadata?.affectedMembers} member(s) affected</span>
          )}
        </span>
        <span className="text-xs text-gray-400 shrink-0 ml-3">{new Date(a.created_at).toLocaleDateString()}</span>
      </div>

      {a.explanation && (
        <p className="text-xs text-gray-500 mt-1 pl-1 border-l-2 border-gray-200">{a.explanation}</p>
      )}

      {mode === null ? (
        <div className="flex gap-3 mt-1">
          <button
            onClick={() => runAction(() => finishUnusualActivity(a.id))}
            disabled={saving}
            className="text-xs font-medium text-reset-terracotta hover:underline disabled:opacity-50"
          >
            Mark finished
          </button>
          <button onClick={() => setMode('refer')} className="text-xs font-medium text-gray-500 hover:underline">
            Refer to a To-Do
          </button>
          <button onClick={() => setMode('explain')} className="text-xs font-medium text-gray-500 hover:underline">
            Add explanation
          </button>
        </div>
      ) : mode === 'refer' ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={text} onChange={e => setText(e.target.value)} rows={2}
            placeholder="Optional — what should the to-do say? Leave blank to use the default."
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          <div className="flex gap-2">
            <button
              onClick={() => runAction(() => referUnusualActivity(a.id, { body: text.trim() || undefined }))}
              disabled={saving}
              className="text-xs font-semibold bg-reset-terracotta text-white px-3 py-1 rounded-lg disabled:opacity-50"
            >
              Create To-Do
            </button>
            <button onClick={() => { setMode(null); setText(''); }} className="text-xs text-gray-400 hover:underline">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <textarea
            value={text} onChange={e => setText(e.target.value)} rows={2}
            placeholder="What actually happened?"
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          <label className="flex items-center gap-1.5 text-xs text-gray-500">
            <input type="checkbox" checked={moveToLog} onChange={e => setMoveToLog(e.target.checked)} />
            Move this to the Bug Log too (otherwise it stays here in Unusual Activity)
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => runAction(() => explainUnusualActivity(a.id, text.trim(), moveToLog))}
              disabled={saving || !text.trim()}
              className="text-xs font-semibold bg-reset-terracotta text-white px-3 py-1 rounded-lg disabled:opacity-50"
            >
              Save
            </button>
            <button onClick={() => { setMode(null); setText(''); }} className="text-xs text-gray-400 hover:underline">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, unit = '', highlight = false, to = null, onClick = null, hint = null }) {
  const content = (
    <>
      <p className={`text-2xl font-bold ${highlight ? 'text-reset-terracotta' : 'text-reset-brown'}`}>
        {value ?? '—'}<span className="text-sm font-normal text-gray-400">{unit}</span>
      </p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
    </>
  );
  const className = `rounded-xl shadow-sm px-4 py-3 text-center block w-full ${highlight ? 'bg-reset-cream' : 'bg-white'} ${(to || onClick) ? 'hover:shadow-md transition-shadow cursor-pointer' : ''}`;
  if (to) return <Link to={to} className={className}>{content}</Link>;
  if (onClick) return <button onClick={onClick} className={className}>{content}</button>;
  return <div className={className}>{content}</div>;
}
